import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Tabs,
    Tab,
    Typography,
    Paper,
    TextField,
    Button,
    Card,
    CardContent,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Chip,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HistoryIcon from '@mui/icons-material/History';
import SecurityIcon from '@mui/icons-material/Security';
import LoadingErrorBoundary from '../components/LoadingErrorBoundary';
import { useAsync } from '../hooks/useAsync';
import { useAuth } from '../contexts/AuthContext';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`user-tabpanel-${index}`}
            aria-labelledby={`user-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const UserDashboard: React.FC = () => {
    const { user } = useAuth();
    const [tabValue, setTabValue] = useState(0);
    const [amount, setAmount] = useState('1');
    
    // Use user preferences for default currencies, fallback to defaults
    const defaultFromCurrency = user?.preferences?.defaultFromCurrency || 'USD';
    const defaultToCurrency = user?.preferences?.defaultToCurrency || 'EUR';
    
    const [fromCurrency, setFromCurrency] = useState(defaultFromCurrency);
    const [toCurrency, setToCurrency] = useState(defaultToCurrency);
    
    console.log('👤 UserDashboard: User preferences:', user?.preferences);
    console.log('👤 UserDashboard: Default currencies:', { defaultFromCurrency, defaultToCurrency });

    // Common currencies list
    const commonCurrencies = [
        'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'SEK', 'NZD',
        'MXN', 'SGD', 'HKD', 'NOK', 'TRY', 'RUB', 'INR', 'BRL', 'ZAR', 'KRW',
        'IRR', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD', 'LBP', 'EGP'
    ];

    // Get currencies to show (user's favorites first, then common currencies)
    const getCurrencyOptions = () => {
        const favoriteCurrencies = user?.preferences?.favoriteCurrencies || [];
        const allCurrencies = [...new Set([...favoriteCurrencies, ...commonCurrencies])];
        return allCurrencies;
    };

    // Update currencies when user preferences change
    useEffect(() => {
        if (user?.preferences) {
            console.log('👤 UserDashboard: Updating currencies from preferences');
            setFromCurrency(user.preferences.defaultFromCurrency || 'USD');
            setToCurrency(user.preferences.defaultToCurrency || 'EUR');
        }
    }, [user?.preferences]);

    const {
        data: activeOffers,
        loading: offersLoading,
        error: offersError,
        execute: fetchOffers,
        retry,
    } = useAsync<typeof mockActiveOffers>();

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Handle FROM currency change - reset amount to 1
    const handleFromCurrencyChange = (newCurrency: string) => {
        setAmount('1');
        setFromCurrency(newCurrency);
    };

    // Handle TO currency change - reset amount to 1
    const handleToCurrencyChange = (newCurrency: string) => {
        setAmount('1');
        setToCurrency(newCurrency);
    };

    // Mock data for the chart
    const chartData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
            {
                label: 'Exchange Rate Trend',
                data: [1.2, 1.25, 1.22, 1.28, 1.26, 1.24],
                borderColor: '#2563EB',
                tension: 0.4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
        },
        scales: {
            y: {
                beginAtZero: false,
            },
        },
    };

    // Mock data for active offers
    const mockActiveOffers = [
        {
            id: 1,
            user: 'John Doe',
            amount: '1,000 USD',
            rate: '1.25 EUR/USD',
            location: 'New York',
            rating: 4.8,
        },
        {
            id: 2,
            user: 'Jane Smith',
            amount: '2,500 USD',
            rate: '1.24 EUR/USD',
            location: 'London',
            rating: 4.9,
        },
    ];

    useEffect(() => {
        // Simulate API call
        const fetchData = async () => {
            try {
                await fetchOffers(
                    new Promise((resolve) => {
                        setTimeout(() => resolve(mockActiveOffers), 1000);
                    })
                );
            } catch (error) {
                console.error('Failed to fetch offers:', error);
            }
        };
        fetchData();
    }, [fetchOffers]);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                User Dashboard
            </Typography>

            <Box sx={{ width: '100%', mt: 4 }}>
                <Paper elevation={3} sx={{ borderRadius: 2 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="user dashboard tabs"
                        variant="fullWidth"
                    >
                        <Tab label="Exchange" />
                        <Tab label="Active Offers" />
                        <Tab label="Transaction History" />
                        <Tab label="Profile" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Currency Calculator
                                        </Typography>
                                        <Box sx={{ mb: 3 }}>
                                            <TextField
                                                fullWidth
                                                label="Amount"
                                                type="number"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                sx={{ mb: 2 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="From"
                                                    value={fromCurrency}
                                                    onChange={(e) => handleFromCurrencyChange(e.target.value)}
                                                    SelectProps={{
                                                        native: true,
                                                    }}
                                                >
                                                    {getCurrencyOptions().map(currency => (
                                                        <option key={currency} value={currency}>{currency}</option>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="To"
                                                    value={toCurrency}
                                                    onChange={(e) => handleToCurrencyChange(e.target.value)}
                                                    SelectProps={{
                                                        native: true,
                                                    }}
                                                >
                                                    {getCurrencyOptions().map(currency => (
                                                        <option key={currency} value={currency}>{currency}</option>
                                                    ))}
                                                </TextField>
                                            </Box>
                                        </Box>
                                        <Button variant="contained" fullWidth>
                                            Calculate
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Exchange Rate Trend
                                        </Typography>
                                        <Box sx={{ height: 300 }}>
                                            <Line data={chartData} options={chartOptions} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <LoadingErrorBoundary
                            loading={offersLoading}
                            error={offersError}
                            onRetry={retry}
                        >
                            <List>
                                {activeOffers?.map((offer) => (
                                    <ListItem
                                        key={offer.id}
                                        divider
                                        sx={{
                                            bgcolor: 'background.paper',
                                            borderRadius: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar>
                                                <PersonIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={`${offer.amount} at ${offer.rate}`}
                                            secondary={`${offer.user} • ${offer.location}`}
                                        />
                                        <Chip
                                            label={`Rating: ${offer.rating}`}
                                            color="primary"
                                            size="small"
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </LoadingErrorBoundary>
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <List>
                            <ListItem divider>
                                <ListItemAvatar>
                                    <Avatar>
                                        <HistoryIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary="Exchange USD to EUR"
                                    secondary="Completed • 1,000 USD → 1,250 EUR"
                                />
                                <Chip label="Completed" color="success" size="small" />
                            </ListItem>
                            <ListItem divider>
                                <ListItemAvatar>
                                    <Avatar>
                                        <AttachMoneyIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary="Exchange EUR to GBP"
                                    secondary="Pending • 500 EUR → 425 GBP"
                                />
                                <Chip label="Pending" color="warning" size="small" />
                            </ListItem>
                        </List>
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Account Information
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            <TextField
                                                fullWidth
                                                label="Email"
                                                value={user?.email || "user@example.com"}
                                                disabled
                                                sx={{ mb: 2 }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Display Name"
                                                value={user?.displayName || "No display name"}
                                                disabled
                                                sx={{ mb: 2 }}
                                            />
                                            <Button variant="outlined" fullWidth>
                                                Edit Profile
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Security Settings
                                        </Typography>
                                        <List>
                                            <ListItem>
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        <SecurityIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary="Two-Factor Authentication"
                                                    secondary="Not enabled"
                                                />
                                                <Button variant="outlined" size="small">
                                                    Enable
                                                </Button>
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>
                    </TabPanel>
                </Paper>
            </Box>
        </Container>
    );
};

export default UserDashboard; 